import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

export interface EmailSettings {
  admin_notification_email: string;
  order_from_email: string;
  support_from_email: string;
  quote_from_email: string;
  noreply_email: string;
  company_email: string;
  company_phone: string;
  company_name: string;
  /** Visningsnamn i From (varumärke). Tomt => company_name. */
  email_sender_display_name: string;
  company_address: string;
  company_org_number: string;
  company_logo_url: string;
  /** Mörk logga för vita dokument (PDF-kvitto). */
  company_logo_print_url: string;
  cc_admin_emails: string[];
  email_signature: string;
}

const defaultSettings: EmailSettings = {
  admin_notification_email: '',
  order_from_email: '',
  support_from_email: '',
  quote_from_email: '',
  noreply_email: '',
  company_email: '',
  company_phone: '',
  company_name: '',
  email_sender_display_name: '',
  company_address: '',
  company_org_number: '',
  company_logo_url: '',
  company_logo_print_url: '',
  cc_admin_emails: [],
  email_signature: '',
};

// In-memory cache
let cachedSettings: EmailSettings | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useEmailSettings = () => {
  const [settings, setSettings] = useState<EmailSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Check if we have valid cached data
        const now = Date.now();
        if (cachedSettings && (now - cacheTimestamp) < CACHE_DURATION) {
          setSettings(cachedSettings);
          setLoading(false);
          return;
        }

        // Fetch from database (omit p_tenant_id för default tenant – undvik explicit null som kan ge 400 i vissa miljöer)
        const { data, error: rpcError } = await supabase.rpc('get_email_settings', {});

        if (rpcError) {
          console.error('Error fetching email settings:', rpcError);
          setError(rpcError);
          setSettings(defaultSettings);
        } else if (data) {
          // Parse cc_admin_emails if it's a string
          const parsedData = {
            ...data,
            cc_admin_emails: Array.isArray(data.cc_admin_emails)
              ? data.cc_admin_emails
              : [],
          };

          // Update cache
          cachedSettings = parsedData;
          cacheTimestamp = now;

          setSettings(parsedData);
        } else {
          setSettings(defaultSettings);
        }
      } catch (err) {
        console.error('Exception fetching email settings:', err);
        setError(err as Error);
        setSettings(defaultSettings);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Function to invalidate cache (useful after updating settings)
  const refreshSettings = async () => {
    cachedSettings = null;
    cacheTimestamp = 0;
    setLoading(true);

    try {
      const { data, error: rpcError } = await supabase.rpc('get_email_settings', {});

      if (rpcError) {
        console.error('Error refreshing email settings:', rpcError);
        setError(rpcError);
        setSettings(defaultSettings);
      } else if (data) {
        const parsedData = {
          ...data,
          cc_admin_emails: Array.isArray(data.cc_admin_emails)
            ? data.cc_admin_emails
            : [],
        };

        cachedSettings = parsedData;
        cacheTimestamp = Date.now();

        setSettings(parsedData);
      }
    } catch (err) {
      console.error('Exception refreshing email settings:', err);
      setError(err as Error);
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  return {
    settings,
    loading,
    error,
    refreshSettings,
  };
};

// Helper function to get settings synchronously (returns cached or default)
export const getEmailSettingsSync = (): EmailSettings => {
  if (cachedSettings) {
    return cachedSettings;
  }
  return defaultSettings;
};
